import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CalendarView = ({ 
  currentDate, 
  scheduledPosts, 
  onDateSelect, 
  onPostDragStart, 
  onPostDrop,
  selectedDate 
}) => {
  const [draggedPost, setDraggedPost] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days?.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days?.push(new Date(year, month, i));
    }
    return days;
  };

  const getPostsForDate = (date) => {
    if (!date) return [];
    const dateStr = date?.toISOString()?.split('T')?.[0];
    return scheduledPosts?.filter(post => 
      post?.scheduledDate?.toISOString()?.split('T')?.[0] === dateStr
    );
  };

  const handleDragStart = (e, post) => {
    setDraggedPost(post);
    onPostDragStart(post);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, date) => {
    e?.preventDefault();
    if (date) {
      setDragOverDate(date?.toISOString()?.split('T')?.[0]);
    }
  };

  const handleDragLeave = () => {
    setDragOverDate(null);
  };

  const handleDrop = (e, date) => {
    e?.preventDefault();
    if (draggedPost && date) {
      onPostDrop(draggedPost, date);
    }
    setDraggedPost(null);
    setDragOverDate(null);
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date?.toDateString() === today?.toDateString();
  };

  const isSelected = (date) => {
    if (!date || !selectedDate) return false;
    return date?.toDateString() === selectedDate?.toDateString();
  };

  const days = getDaysInMonth(currentDate);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const platformIcons = {
    facebook: 'Facebook',
    instagram: 'Instagram',
    twitter: 'Twitter',
    linkedin: 'Linkedin',
    tiktok: 'Music'
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="grid grid-cols-7 border-b border-border">
        {weekDays?.map(day => (
          <div 
            key={day} 
            className="px-2 py-3 text-center text-sm font-semibold text-foreground bg-muted"
          >
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days?.map((date, index) => {
          const posts = getPostsForDate(date);
          const dateStr = date ? date?.toISOString()?.split('T')?.[0] : null;
          const isDragOver = dragOverDate === dateStr;

          return (
            <div
              key={index}
              className={`min-h-32 border-r border-b border-border p-2 transition-colors ${
                !date ? 'bg-muted/30' : 'bg-card hover:bg-muted/50 cursor-pointer'
              } ${isToday(date) ? 'bg-primary/5' : ''} ${
                isSelected(date) ? 'ring-2 ring-primary ring-inset' : ''
              } ${isDragOver ? 'bg-accent/20 ring-2 ring-accent ring-inset' : ''}`}
              onClick={() => date && onDateSelect(date)}
              onDragOver={(e) => handleDragOver(e, date)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, date)}
            >
              {date && (
                <>
                  <div className={`text-sm font-medium mb-2 ${
                    isToday(date) ? 'text-primary' : 'text-foreground'
                  }`}>
                    {date?.getDate()}
                  </div>

                  <div className="space-y-1">
                    {posts?.slice(0, 3)?.map(post => (
                      <div
                        key={post?.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, post)}
                        className="bg-background border border-border rounded p-1.5 cursor-move hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start gap-1.5">
                          {post?.media && (
                            <div className="w-8 h-8 rounded overflow-hidden flex-shrink-0">
                              <Image
                                src={post?.media}
                                alt={post?.mediaAlt}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-foreground line-clamp-2 mb-1">
                              {post?.content}
                            </p>
                            <div className="flex items-center gap-1">
                              {post?.platforms?.map(platform => (
                                <Icon
                                  key={platform}
                                  name={platformIcons?.[platform]}
                                  size={12}
                                  className="text-muted-foreground"
                                />
                              ))}
                              <span className="text-xs text-muted-foreground ml-auto">
                                {post?.scheduledDate?.toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {posts?.length > 3 && (
                      <div className="text-xs text-muted-foreground text-center py-1">
                        +{posts?.length - 3} more
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarView;